import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appSettings } from '../../../environments/app-settings';
import { disableErrorHandler } from '../../http/error.interceptor';
import { EntityAction } from '../../models/entity-action';
import { ExternalApplicationBase } from '../../models/external-application-base';
import { Fileshare } from '../../models/fileshare';
import { PageResult } from '../../models/page-result';
import { Storage } from '../../models/storage';
import { TableConfiguration } from '../../models/table-configuration';
import { CompletionInfo } from '../models/completion-info';
import { DirectUpload } from '../models/direct-upload';
import { File as UploadFile } from '../models/files';
import { Upload } from '../models/upload';
import { WorkItem } from '../models/work-item';

/**
 * Data upload file information.
 */
export interface UploadFileInfo {
  /**
   * File ID. GUID.
   * Issued by SHC File Upload Service.
   */
  id: string;
  /**
   * Original file name.
   */
  name: string;
  /**
   * File size in bytes.
   */
  size: number;
  /**
   * Specifies if file can be removed.
   */
  canDelete: boolean;
  /**
   * UTC date time of upload URL expiration.
   */
  expiresOn: Date;
}

/**
 * Encapsulates data upload related operations.
 */
@Injectable()
export class UploadService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${appSettings.api.url}ui/uploads`;
  private readonly includeFiles = false;

  /**
   * Retrieves page of data upload.
   *
   * @param sortBy Name of the field for ordering.
   * @param isDescending Flag which displays if ordering should be descending.
   * @param searchText Search text.
   * @param filters Array of statuses to display.
   * @param continuationToken Next page token.
   * @returns A page of data upload.
   */
  public getPage(sortBy: string, isDescending: boolean, searchText: string, filters: string[], continuationToken: string): Observable<PageResult<WorkItem>> {
    let params = new HttpParams()
      .set('sortBy', sortBy)
      .set('isDescending', isDescending.toString());

    if (searchText)
      params = params.set('searchText', searchText);

    for (const filter of filters)
      params = params.append('filter', filter);

    let headers = new HttpHeaders();
    if (continuationToken)
      headers = headers.set('x-syk-continuation', continuationToken);

    return this.http.get<PageResult<WorkItem>>(this.baseUrl, { params, headers });
  }

  /**
   * Retrieves blank data upload with allowed actions list.
   *
   * @param collectionId Related data collection ID.
   * @returns Blank data upload.
   */
  public blank(collectionId: string = null): Observable<EntityAction<Upload>> {
    return collectionId
      ? this.http.get<EntityAction<Upload>>(`${this.baseUrl}/collections/${collectionId}/blank`)
      : this.http.get<EntityAction<Upload>>(`${this.baseUrl}/blank`);
  }

  /**
   * Retrieves existing data upload with allowed actions list.
   *
   * @param id Data upload ID.
   * @param collectionId Related data collection ID.
   * @returns Data upload.
   */
  public get(id: string, collectionId: string): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('files', this.includeFiles);

    return this.http.get<EntityAction<Upload>>(`${this.baseUrl}/${id}/collections/${collectionId}`, { params });
  }

  /**
   * Adds new data upload.
   *
   * @param upload Data upload to add.
   * @returns Actual data upload.
   */
  public create(upload: Upload): Observable<EntityAction<Upload>> {
    return this.http.post<EntityAction<Upload>>(this.baseUrl, upload);
  }

  /**
   * Get data upload files.
   *
   * @param id Data upload ID.
   * @param collectionId Related data collection ID.
   * @returns Data upload files.
   */
  public getFiles(id: string, collectionId: string): Observable<UploadFile[]> {
    return this.http.get<UploadFile[]>(`${this.baseUrl}/${id}/collections/${collectionId}/files`);
  }

  /**
   * Get data upload file.
   *
   * @param upload Data upload.
   * @param fileID Data upload file ID.
   * @returns Actual data upload file.
   */
  public getFile(upload: Upload, fileID: string): Observable<UploadFile> {
    return this.http.get<UploadFile>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files/${fileID}`, { context: disableErrorHandler() });
  }

  /**
   * Add data upload file.
   *
   * @param upload Data upload.
   * @param file Data upload file.
   * @returns Actual data upload file.
   */
  public addFile(upload: Upload, file: UploadFileInfo): Observable<UploadFile> {
    return this.http.post<UploadFile>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files`, file, { context: disableErrorHandler() });
  }

  /**
   * Remove data upload file.
   *
   * @param upload Data upload.
   * @param fileID Data upload file ID.
   */
  public deleteFile(upload: Upload, fileID: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files/${fileID}`, { context: disableErrorHandler() });
  }

  /**
   * Initiates data upload file ingestion.
   *
   * @param upload Data upload.
   * @param fileID Data upload file ID.
   * @returns Actual data upload file.
   */
  public beginFileIngestion(upload: Upload, fileID: string): Observable<UploadFile> {
    return this.http.put<UploadFile>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/files/${fileID}/begin-ingestion`, null);
  }

  /**
   * Submits data upload.
   *
   * @param upload Data upload.
   * @returns Actual data upload.
   */
  public submit(upload: Upload): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('files', this.includeFiles);

    return this.http.put<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/submit`, null, { params });
  }

  /**
   * Sets data upload pending.
   *
   * @param upload Data upload.
   * @returns Actual data upload.
   */
  public setPending(upload: Upload): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('files', this.includeFiles);

    return this.http.put<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/pending`, null, { params });
  }

  /**
   * Completes data upload.
   *
   * @param upload Data upload.
   * @param info Completion information.
   * @returns Actual data upload.
   */
  public complete(upload: Upload, info: CompletionInfo): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('files', this.includeFiles);

    return this.http.put<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}/complete`, info, { params });
  }

  /**
   * Cancels data upload.
   *
   * @param upload Data upload.
   * @param reason Cancellation reason.
   * @returns Actual data upload.
   */
  public cancel(upload: Upload, reason: string): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('reason', reason)
      .set('files', this.includeFiles);

    return this.http.delete<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}`, { params });
  }

  /**
   * Updates existing data upload properties.
   *
   * @param upload Updated data upload.
   * @returns Actual data upload.
   */
  public update(upload: Upload): Observable<EntityAction<Upload>> {
    const params = new HttpParams()
      .set('files', this.includeFiles);

    return this.http.put<EntityAction<Upload>>(`${this.baseUrl}/${upload.id}/collections/${upload.uploadInfo.collection.id}`, upload, { params });
  }

  /**
   * Retrieves data upload file shares.
   *
   * @param id Data upload ID.
   * @param collectionId Related data collection ID.
   * @returns Data upload file shares.
   */
  public getFileshares(id: string, collectionId: string): Observable<Storage<Fileshare>> {
    return this.http.get<Storage<Fileshare>>(`${this.baseUrl}/${id}/collections/${collectionId}/fileshares`, { context: disableErrorHandler() });
  }

  /**
   * Retrieves data upload external application.
   *
   * @param id Data upload ID.
   * @param collectionId Related data collection ID.
   * @returns Data upload external application.
   */
  public getExternalApplication(id: string, collectionId: string): Observable<ExternalApplicationBase> {
    return this.http.get<ExternalApplicationBase>(`${this.baseUrl}/${id}/collections/${collectionId}/app`, { context: disableErrorHandler() });
  }

  /**
   * Retrieves direct (script) upload.
   *
   * @param id Data upload ID.
   * @param collectionId Related data collection ID.
   * @returns Direct (script).
   */
  public getDirectUpload(id: string, collectionId: string): Observable<DirectUpload> {
    return this.http.get<DirectUpload>(`${this.baseUrl}/${id}/collections/${collectionId}/script`);
  }

  /**
   * Retrieves configuration data for upload table with allowed actions list.
   *
   * @returns EntityAction instance with TableConfiguration.
   */
  public getConfiguration(): Observable<EntityAction<TableConfiguration>> {
    return this.http.get<EntityAction<TableConfiguration>>(`${this.baseUrl}/configuration`);
  }
}
